import { useEffect, useState, useRef } from "react";

// ui
import {
  Box,
  Button,
  Modal,
  Typography,
  Card,
  CardContent,
  IconButton,
  TextField,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

// dnd
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

// API
import { getSaleCompanyGroups, putSaleCompanyChart } from "../api/saleCompanyGroup";
import { getAccountsBySaleCompany } from "../api/account";

// jotai
import { useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";

// ローカルstate型定義
type LocalGroup = {
  id: number | null;
  tempId: string;
  sale_company_group_name: string;
  action: "keep" | "create" | "update" | "delete";
};

type LocalAccountAssignment = {
  account_id: number;
  sale_company_group_id: number | null | string;
};

interface SaleCompanyChartProps {
  onModal: boolean;
  swCloseModal: () => void;
  saleCompanyId: string;
  saleCompanyName: string;
}

interface Account {
  id: string;
  account_name: string;
  sale_company_group_id: string | null;
  [key: string]: any;
}

interface Column {
  droppableId: string;
  groupKey: string | null;
  groupName: string;
  accounts: Account[];
}

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90vw",
  maxWidth: "1200px",
  maxHeight: "85vh",
  bgcolor: "background.paper",
  border: "2px solid #ccc",
  boxShadow: 24,
  p: 3,
  borderRadius: 1,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column" as const,
};

export function SaleCompanyChart(props: SaleCompanyChartProps) {
  const { onModal, swCloseModal, saleCompanyId, saleCompanyName } = props;
  const setIsLoading = useSetAtom(AppAtoms.isLoading);

  // 変更1: モーダル表示はデータ取得完了後
  const [showModal, setShowModal] = useState(false);

  // 変更2: ローカルstateでグループ・アカウント所属を管理
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);
  const [localGroups, setLocalGroups] = useState<LocalGroup[]>([]);
  const [localAccountAssignments, setLocalAccountAssignments] = useState<LocalAccountAssignment[]>([]);

  const [error, setError] = useState<string>("");
  const [updateResult, setUpdateResult] = useState<"" | "success" | "error">("");

  // グループ追加UI
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  // グループ編集UI（グループキーで識別）
  const [editingGroupKey, setEditingGroupKey] = useState<string | null>(null);
  const [editGroupName, setEditGroupName] = useState("");

  const nextTempIdRef = useRef(1);

  useEffect(() => {
    if (onModal && saleCompanyName) {
      loadData();
    } else if (!onModal) {
      setShowModal(false);
    }
  }, [onModal, saleCompanyName]);

  async function loadData() {
    setError("");
    // 変更1: モーダルを非表示のままグローバルローディング表示
    setIsLoading(true);
    nextTempIdRef.current = 1;
    try {
      const [groupsResult, accountsResult] = await Promise.all([
        getSaleCompanyGroups(saleCompanyId),
        getAccountsBySaleCompany(saleCompanyName),
      ]);

      const groups = (groupsResult?.sudDatas ?? []) as {
        id: number;
        sale_company_group_name: string;
      }[];
      const accounts = (accountsResult ?? []) as Account[];

      setAllAccounts(accounts);
      setLocalGroups(
        groups.map((g) => ({
          id: g.id,
          tempId: "",
          sale_company_group_name: g.sale_company_group_name,
          action: "keep" as const,
        }))
      );
      setLocalAccountAssignments(
        accounts.map((a) => ({
          account_id: Number(a.id),
          sale_company_group_id:
            a.sale_company_group_id && a.sale_company_group_id !== "0"
              ? Number(a.sale_company_group_id)
              : null,
        }))
      );

      // 変更1: データ取得完了後にモーダル表示
      setShowModal(true);
    } catch {
      setError("データの取得に失敗しました。");
    } finally {
      setIsLoading(false);
    }
  }

  // グループキー取得（既存グループはid、新規はtempId）
  function getGroupKey(g: LocalGroup): string {
    return g.id !== null ? String(g.id) : g.tempId;
  }

  // 表示用カラムをローカルstateから算出
  const visibleGroups = localGroups.filter((g) => g.action !== "delete");

  const columns: Column[] = [
    {
      droppableId: "unassigned",
      groupKey: null,
      groupName: "未所属",
      accounts: allAccounts.filter((a) => {
        const ass = localAccountAssignments.find(
          (x) => x.account_id === Number(a.id)
        );
        return !ass || ass.sale_company_group_id === null;
      }),
    },
    ...visibleGroups.map((g) => {
      const key = getGroupKey(g);
      return {
        droppableId: key,
        groupKey: key,
        groupName: g.sale_company_group_name,
        accounts: allAccounts.filter((a) => {
          const ass = localAccountAssignments.find(
            (x) => x.account_id === Number(a.id)
          );
          if (!ass) return false;
          if (g.id !== null) {
            return ass.sale_company_group_id === g.id;
          } else {
            return ass.sale_company_group_id === g.tempId;
          }
        }),
      };
    }),
  ];

  // 変更2: DnD操作はローカルstateのみ更新（API呼び出しなし）
  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    const srcCol = columns.find((c) => c.droppableId === source.droppableId);
    if (!srcCol) return;
    const movedAccount = srcCol.accounts[source.index];
    if (!movedAccount) return;

    const dstCol = columns.find((c) => c.droppableId === destination.droppableId);
    if (!dstCol) return;

    let newGroupId: number | null | string = null;
    if (dstCol.groupKey !== null) {
      const dstGroup = visibleGroups.find((g) => getGroupKey(g) === dstCol.groupKey);
      if (dstGroup) {
        newGroupId = dstGroup.id !== null ? dstGroup.id : dstGroup.tempId;
      }
    }

    setLocalAccountAssignments((prev) =>
      prev.map((ass) =>
        ass.account_id === Number(movedAccount.id)
          ? { ...ass, sale_company_group_id: newGroupId }
          : ass
      )
    );
  };

  // 変更2: グループ追加はローカルstateのみ
  function handleAddGroupLocal() {
    if (!newGroupName.trim()) return;
    const tempId = `NEW_${nextTempIdRef.current++}`;
    setLocalGroups((prev) => [
      ...prev,
      {
        id: null,
        tempId,
        sale_company_group_name: newGroupName.trim(),
        action: "create",
      },
    ]);
    setNewGroupName("");
    setAddingGroup(false);
  }

  // 変更2: グループ名変更はローカルstateのみ
  function handleEditGroupLocal(groupKey: string) {
    if (!editGroupName.trim()) return;
    setLocalGroups((prev) =>
      prev.map((g) => {
        if (getGroupKey(g) === groupKey) {
          return {
            ...g,
            sale_company_group_name: editGroupName.trim(),
            action: g.action === "keep" ? "update" : g.action,
          };
        }
        return g;
      })
    );
    setEditingGroupKey(null);
    setEditGroupName("");
  }

  // 変更2: グループ削除はローカルstateのみ（所属アカウントは自動で未所属に）
  function handleDeleteGroupLocal(groupKey: string) {
    setLocalAccountAssignments((prev) =>
      prev.map((ass) => {
        const assGroupId = ass.sale_company_group_id;
        const matches =
          typeof assGroupId === "number"
            ? String(assGroupId) === groupKey
            : assGroupId === groupKey;
        if (matches) {
          return { ...ass, sale_company_group_id: null };
        }
        return ass;
      })
    );
    setLocalGroups((prev) =>
      prev.map((g) => {
        if (getGroupKey(g) === groupKey) {
          return { ...g, action: "delete" };
        }
        return g;
      })
    );
  }

  // 変更3: 更新ボタン — 全変更を一括送信
  async function handleUpdate() {
    setShowModal(false);
    swCloseModal();
    setIsLoading(true);
    try {
      await putSaleCompanyChart({
        sale_company_id: Number(saleCompanyId),
        groups: localGroups
          .filter((g) => g.action !== "keep")
          .map((g) => ({
            id: g.id,
            sale_company_group_name: g.sale_company_group_name,
            action: g.action,
            tempId: g.tempId || null,
          })),
        account_assignments: localAccountAssignments.map((a) => ({
          account_id: a.account_id,
          sale_company_group_id: a.sale_company_group_id,
        })),
      });
      setUpdateResult("success");
    } catch {
      setUpdateResult("error");
    } finally {
      setIsLoading(false);
    }
  }

  const resultModalStyle = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "400px",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
  };

  return (
    <>
    <Modal
      open={updateResult !== ""}
      aria-labelledby="sale-company-chart-result-title"
    >
      <Box sx={resultModalStyle}>
        <Typography variant="h6" id="sale-company-chart-result-title" sx={{ mb: 2 }}>
          体制図更新
        </Typography>
        <Typography sx={{ mb: 3 }}>
          {updateResult === "success"
            ? "更新が完了しました。"
            : "更新に失敗しました。再度お試しください。"}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setUpdateResult("")}
          fullWidth
        >
          閉じる
        </Button>
      </Box>
    </Modal>
    <Modal open={showModal} aria-labelledby="sale-company-chart-title">
      <Box sx={modalStyle}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography id="sale-company-chart-title" variant="h6">
            体制図
          </Typography>
        </Box>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* 横スクロール対応 */}
        <Box sx={{ overflowX: "auto", flex: 1, pb: 1 }}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                minHeight: "400px",
                alignItems: "flex-start",
                width: "max-content",
              }}
            >
              {columns.map((col) => (
                <Box
                  key={col.droppableId}
                  sx={{
                    minWidth: "200px",
                    maxWidth: "240px",
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                    p: 1,
                    flexShrink: 0,
                  }}
                >
                  {/* カラムヘッダー */}
                  <Box
                    sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    {col.groupKey !== null &&
                    editingGroupKey === col.groupKey ? (
                      <>
                        <TextField
                          size="small"
                          value={editGroupName}
                          onChange={(e) => setEditGroupName(e.target.value)}
                          sx={{
                            flex: 1,
                            "& .MuiInputBase-input": { fontSize: "12px" },
                          }}
                          autoFocus
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleEditGroupLocal(col.groupKey as string)
                          }
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setEditingGroupKey(null)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <Typography
                          variant="subtitle2"
                          sx={{ flex: 1, fontWeight: "bold" }}
                        >
                          {col.groupName}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 0.5, color: "text.secondary" }}
                          >
                            ({col.accounts.length})
                          </Typography>
                        </Typography>
                        {col.groupKey !== null && (
                          <>
                            <IconButton
                              size="small"
                              sx={{ p: 0.3 }}
                              onClick={() => {
                                setEditingGroupKey(col.groupKey as string);
                                setEditGroupName(col.groupName);
                              }}
                            >
                              <EditIcon sx={{ fontSize: "14px" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ p: 0.3 }}
                              onClick={() =>
                                handleDeleteGroupLocal(col.groupKey as string)
                              }
                            >
                              <DeleteIcon sx={{ fontSize: "14px" }} />
                            </IconButton>
                          </>
                        )}
                      </>
                    )}
                  </Box>

                  {/* アカウントカード一覧 */}
                  <Droppable droppableId={col.droppableId}>
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          minHeight: "100px",
                          bgcolor: snapshot.isDraggingOver
                            ? "#e3f2fd"
                            : "transparent",
                          borderRadius: 1,
                          p: 0.5,
                          transition: "background-color 0.2s",
                        }}
                      >
                        {col.accounts.map((account, index) => (
                          <Draggable
                            key={String(account.id)}
                            draggableId={String(account.id)}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <Card
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                sx={{
                                  mb: 0.5,
                                  boxShadow: snapshot.isDragging ? 4 : 1,
                                  bgcolor: snapshot.isDragging
                                    ? "#bbdefb"
                                    : "#fff",
                                  cursor: "grab",
                                }}
                              >
                                <CardContent sx={{ p: "6px 10px !important" }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontSize: "12px" }}
                                  >
                                    {account.account_name ||
                                      `ID: ${account.id}`}
                                  </Typography>
                                </CardContent>
                              </Card>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Box>
              ))}

              {/* グループ追加カラム */}
              <Box sx={{ minWidth: "180px", flexShrink: 0 }}>
                {addingGroup ? (
                  <Box sx={{ bgcolor: "#f5f5f5", borderRadius: 1, p: 1 }}>
                    <TextField
                      size="small"
                      placeholder="グループ名"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddGroupLocal();
                      }}
                      autoFocus
                      fullWidth
                      sx={{
                        mb: 1,
                        "& .MuiInputBase-input": { fontSize: "12px" },
                      }}
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleAddGroupLocal}
                      >
                        追加
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setAddingGroup(false);
                          setNewGroupName("");
                        }}
                      >
                        キャンセル
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setAddingGroup(true)}
                    size="small"
                    sx={{ height: "36px" }}
                  >
                    グループ追加
                  </Button>
                )}
              </Box>
            </Box>
          </DragDropContext>
        </Box>

        {/* 変更3: フッター — 更新・キャンセルボタン */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 2,
            pt: 2,
            borderTop: "1px solid #eee",
          }}
        >
          <Button variant="outlined" onClick={handleUpdate}>
            更新
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setShowModal(false);
              swCloseModal();
            }}
          >
            キャンセル
          </Button>
        </Box>
      </Box>
    </Modal>
    </>
  );
}
